import { RefreshCw, Play } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";

/** Convert ISO string → "3m ago" / "2h ago" / "5d ago". Null-safe. */
function formatAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

/**
 * CoverageSnapshotTile — presentational card matching the
 * "Editorial reviews backfill" pattern, but for one-off / batch
 * generators where a live pace isn't available. Given a `snapshot`
 * object with `title`, `by_country` and `recent`, it renders the same
 * two-panel coverage layout used by ReviewsBackfillTile.
 */
export default function CoverageSnapshotTile({
  icon: Icon,
  tone = "emerald",
  daemonHint,
  daemonProgram,     // supervisor program name enabling "Run 30 min"
  snapshot,
  onRefresh,
  testId,
  recentLabelKey = "title",
}) {
  const [bursting, setBursting] = useState(false);
  const [burstMsg, setBurstMsg] = useState(null);
  if (!snapshot) return null;
  const totalFilled = (snapshot.by_country || []).reduce(
    (n, c) => n + (c.filled || 0),
    0,
  );
  const lastAgo = formatAgo(snapshot.last_updated_at);

  async function handleBurst() {
    if (!daemonProgram) return;
    setBursting(true);
    setBurstMsg(null);
    try {
      const r = await api.post(
        `/admin/progress/burst-daemon?program=${encodeURIComponent(daemonProgram)}&minutes=30`,
      );
      setBurstMsg(`Started · auto-stop ${new Date(r.data.stops_at_utc).toLocaleTimeString()}`);
    } catch (e) {
      setBurstMsg(e?.response?.data?.detail || "Failed to burst");
    } finally {
      setBursting(false);
      onRefresh && onRefresh();
    }
  }
  const iconBg = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    sky: "bg-sky-50 text-sky-700",
    rose: "bg-rose-50 text-rose-700",
    violet: "bg-violet-50 text-violet-700",
  }[tone] || "bg-stone-100 text-stone-700";
  const barColour = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    sky: "bg-sky-500",
    rose: "bg-rose-500",
    violet: "bg-violet-500",
  }[tone] || "bg-stone-500";

  return (
    <div
      data-testid={testId}
      className="border border-stone-200 rounded-xl p-5 bg-white shadow-sm"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          {Icon && (
            <div className={`p-2 rounded-lg ${iconBg.split(" ")[0]}`}>
              <Icon className={`h-5 w-5 ${iconBg.split(" ")[1]}`} />
            </div>
          )}
          <div>
            <div className="text-sm font-semibold text-stone-900">
              {snapshot.title}
            </div>
            {daemonHint && (
              <div className="text-xs text-stone-500">{daemonHint} · {totalFilled.toLocaleString()} filled so far</div>
            )}
            {lastAgo && (
              <div
                className="text-[11px] text-stone-500 mt-0.5"
                data-testid={`${testId}-last-updated`}
                title={snapshot.last_updated_at}
              >
                Last update: <span className="text-stone-800 font-medium">{lastAgo}</span>
              </div>
            )}
          </div>
        </div>
        {onRefresh && (
          <div className="flex items-center gap-2 shrink-0">
            {daemonProgram && (
              <button
                onClick={handleBurst}
                disabled={bursting}
                className="text-[11px] text-emerald-800 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 border border-emerald-200 rounded px-2 py-1 flex items-center gap-1"
                title={`Start ${daemonProgram} for 30 min`}
                data-testid={`${testId}-burst-btn`}
              >
                <Play className="h-3 w-3" /> Run 30m
              </button>
            )}
            <button
              onClick={onRefresh}
              className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1"
              data-testid={`${testId}-refresh-btn`}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
        )}
      </div>
      {burstMsg && (
        <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50/60 border border-emerald-100 rounded px-2 py-1 inline-block">
          {burstMsg}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        {(snapshot.by_country || []).map((c) => (
          <div
            key={c.country}
            data-testid={`${testId}-${c.country.toLowerCase()}`}
            className="rounded-lg border border-stone-100 p-3"
          >
            <div className="text-xs text-stone-500 mb-1">
              {c.country} · {snapshot.title}
            </div>

            {/* Narrated bar (headline number kept for continuity) */}
            <div className="text-2xl font-semibold text-stone-900">
              {(c.narrated?.filled ?? c.filled ?? 0).toLocaleString()} /{" "}
              {(c.narrated?.total  ?? c.total  ?? 0).toLocaleString()}
            </div>
            <div className="mt-2 h-1.5 bg-stone-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${barColour} rounded-full transition-all`}
                style={{ width: `${Math.min(100, c.narrated?.pct ?? c.coverage_pct ?? 0)}%` }}
              />
            </div>
            <div className="text-[11px] text-stone-500 mt-1">
              {c.narrated?.pct ?? c.coverage_pct ?? 0}% of narrated subset
              <span className="text-stone-400"> · narrated = editorially reviewed pages</span>
            </div>

            {/* Catalogue bar — sibling metric: true share of the whole
                country's inventory. Same tone as the narrated bar but
                at 55% opacity so operators read the two as a pair. */}
            {c.catalogue && (
              <>
                <div className="mt-3 text-[11px] font-medium text-stone-700">
                  {c.catalogue.filled.toLocaleString()} / {c.catalogue.total.toLocaleString()}
                  <span className="text-stone-500 font-normal">
                    {" — "}{c.catalogue.pct}% of whole {c.country} catalogue
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${barColour} rounded-full transition-all opacity-55`}
                    style={{ width: `${Math.min(100, c.catalogue.pct || 0)}%` }}
                  />
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {(snapshot.recent || []).length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-stone-500 mb-2">Latest 3</div>
          <ul className="space-y-1">
            {snapshot.recent.map((r, i) => (
              <li
                key={i}
                className="text-xs flex items-center gap-2 text-stone-700"
              >
                <span className="inline-block w-8 text-center rounded bg-stone-100 text-stone-600 text-[10px] font-medium py-0.5">
                  {r.country || "—"}
                </span>
                <span className="truncate flex-1">
                  {r[recentLabelKey] || r.title || r.name || r.id}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
